import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CourseStatusTable from '../../../components/tables/CourseStatusTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useCourseStatusApi } from '../../../hooks/useCourseStatusApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { CourseStatusDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const CourseStatusList: React.FC = () => {
  const { search, remove, isLoading, error } = useCourseStatusApi();
  const [items, setItems] = useState<CourseStatusDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Course Statuses', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Course Statuses:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: CourseStatusDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting CourseStatus:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/coursestatuses"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.CourseStatus.View"
    >
      <Card
        title="Manage Course Statuses"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/coursestatuses/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <CourseStatusTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/coursestatuses/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default CourseStatusList;
